import {
	Divider,
	Button,
	Flex,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	Box,
	useToast,
	Textarea,
	Badge,
} from "@chakra-ui/react";
import IssbContainer from "./IssbContainer";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { H1 } from "components/text/headings";
import reportDocService, { baseIdFromTypedId } from "api/issb/reportdoc.service";
import { useQuery, useQueryClient } from "react-query";
import { useAuth } from "context";
import PageHeader from "components/text/PageHeader";
import WizardStepWrapper from "./WizardStepWrapper";
import AIReviewQuestionsPanel from "./AIReview/AIReviewQuestionsPanel";
import ReportDocSourcesPanel from "./ReportDocSourcesPanel";
import { useRerender } from "rerenderer";
import RichTextEditor from "./IssbDataInput/components/RichTextEditor/RichTextEditor";
import DevOnly from "context/Auth/DevOnly";
import { WideMarginsWrapper } from "./IssbDataInput/AnswerDetailPage";
import LocalSpinner from "components/LocalSpinner";
import {
	setAIContext,
	updateAIContext,
	AGENT_CANVAS,
	get_json_content,
	apiGetThreads,
	postChat,
	postReportWriter
} from "api/ai/ai.service";
import _ from "lodash";
import {
	ArrowCircleUp,
	Copy,
	Files,
	Sparkle,
	Chat,
	ThumbsUp,
	ThumbsDown,
	ClockCounterClockwise,
	ShareFat,
	Trash,
} from "@phosphor-icons/react";
import { colors } from "theme/foundations/colors";
import { showSuccessToast } from "utils";
import { ReportDocHeader } from "./ReportDocListPage";
import { routes } from "config/paths";
import { useConfirmation } from "context/Mutation/ConfirmationDialogContext";
import LifecycleState from "api/common/LifecycleState";
import StatusToggle from "pages/ISSB/IssbDataInput/components/StatusToggle";
import { toTitleCase } from "utils/text-utils";
import { Card } from "components/Card";
import { H3 } from "components/text/headings";
import ReportDocEditorPanel, { getFileinfoSpec, ReportDocStateToggle } from "./ReportDocEditorPanel";
import { TCFD_REPORT_TEMPLATE } from "data/templates/template-reports";
import { FaSpinner } from "react-icons/fa";
import PropInput from "components/PropInput";

const STEPS = ["SELECT_SOURCES", "SELECT_QUESTIONS", "FORMAT", "GENERATING", "EDIT_REPORT"];

/**
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=5658-164594&t=VmvkPaqqmcS7OkCY-1
 *
 * @returns
 */
export default function ReportDocEditorPage() {
	const { getAccessTokenSilently } = useAuth();
	const { id: reportDocId } = useParams();
	if (!reportDocId) {
		// this should never happen
		console.error("No report doc id");
	}

	const [stepIndex, setStepIndex] = useState(0);
	const step = STEPS[stepIndex];
	const [_nextDisabled, setNextDisabled] = useState(false);
	let nextDisabled = _nextDisabled;

	const [questionIds, setQuestionIds] = useState([]);
	const [isProcessing, setIsProcessing] = useState(false);

	const queryClient = useQueryClient();
	/** move to the next step + AI generate the draft report if that is the next step! */
	const gotoNext = async () => {
		const newStepIndex = stepIndex + 1;
		setStepIndex(newStepIndex);
		const newStep = STEPS[newStepIndex];
		if (newStep === "GENERATING") {
			if (isProcessing) {
				console.warn("(gotoNext) dupe call? Skipping generation because we are already processing");
				return;
			}
			if (format && !reportDoc.title) reportDoc.title = toTitleCase(format); // paranoia
			if (format === BLANK_REPORT_FORMAT) {
				// no report-writer AI for blank - go straight to editing
				reportDoc.content = "";
				reportDoc.state = LifecycleState.DRAFT;
				await reportDocService.update(reportDocId, reportDoc);
				setStepIndex(4);
				return;
			}
			setIsProcessing(true);
			const axiosConfig = {
				params: {
					byAI: true,
					aiId: "template", // count the initial template as "AI" (since we dont have a marker for "by template")
				},
			};
			await reportDocService.update(reportDocId, reportDoc, axiosConfig); // save the title
			// kick off the generation	  
			const _fileinfoSpec = getFileinfoSpec(reportDoc);
			let message = "Generate a canvas report in the format: " + format;
			if (additionalInstruction) {
				message += "\n\nWith the following additional instructions: " + additionalInstruction;
			}
			// HACK extra nudge for TODOs (see ESG--, sep 2025)
			if (reportDoc.content?.includes("TODO")) {
				message += "\n\nFill in all TODOs in one canvas_edit. If data is not available, write a draft placeholder.";
			}
			const response = await postReportWriter({
				message,
				fileinfoSpec: _fileinfoSpec,
				agent: AGENT_CANVAS,
				reportdoc_id: reportDocId
			});
			// this just the Task info - not the actual report
			console.log("response", response);
			// reload the report doc to get the draft content
			queryClient.invalidateQueries(["reportDoc", reportDocId]);
		}
	}; // ./gotoNext

	// load the report doc
	// A reportDoc is always made and saved before we get to this page - by createReportDocAndGotoEditor()
	const { data: reportDoc, isLoading } = useQuery({
		queryKey: ["reportDoc", reportDocId],
		queryFn: async () => {
			await getAccessTokenSilently();
			return reportDocService.get(reportDocId);
		},
		onSuccess: (reportDoc) => {
			console.log("reportDoc loaded", reportDoc);
			// go to edting?
			if (reportDoc.state === LifecycleState.DRAFT) {
				setIsProcessing(false);
				setStepIndex(4);
			}
		},
		enabled: !!reportDocId,
		refetchInterval: isProcessing ? 5000 : false, // while the AI is processing, refetch every 5 seconds.
		// But don't refetch while the user is editing as that could overwrite and lose their edits
	});

	// HACK skip the first steps if we return to an in-progress wizard or an existing report
	// Note: in useEffect - but this should be safe to re-run as it only applies if stepIndex==0
	const [is1stLoad, setIs1stLoad] = useState(true); //HACK to monitor the first load of the page.
	useEffect(() => {
		if (!reportDoc) return; // loading
		if (stepIndex !== 0) return;
		if (!is1stLoad) return; // If the user did a couple of steps - then went back with (Previous) - don't skip them on again
		setIs1stLoad(false);
		// Have we done the 1st step?
		if (!reportDoc.sourcesAllowed?.length) return;
		let newStepIndex = 1;
		// stepIndex was 0 but we had sources -- must be the user returning to an in-progress report (possibly via a page refresh)
		console.log("Returning to a report that is past step 0", reportDoc);
		// Have we done the 2nd step?
		if (reportDoc.questionFilter) {
			newStepIndex = 2; // on to pick format
		}
		// Have we done the 3rd step? Pcik-format will set the title
		if (reportDoc.title) {
			newStepIndex = 3; // generating Note: if a-thread-is-running would be a better but more cumbersome check
		}
		// Have we done the 4th step?
		if (reportDoc.content || reportDoc.state === LifecycleState.DRAFT || reportDoc.state === LifecycleState.ACTIVE) {
			// Note: content gets set by the template before the report is ready for editing. The AI backend will set state from PROCESSING to DRAFT
			if (reportDoc.state === LifecycleState.PROCESSING) {
				console.log("AI processing not yet done - stay at generating");
				newStepIndex = 3;
			} else {
				newStepIndex = 4; // edit report
			}
		}
		// This is what step we're at
		setStepIndex(newStepIndex);
	}, [reportDoc, stepIndex, is1stLoad]); // ./useEffect for skip-first-steps-if-returning/refreshing

	// set and save sources and questions
	const { rerender } = useRerender();
	const sourceTypedIds = reportDoc?.sourcesAllowed || [];
	const setSourceTypedIds = (newSourceTypedIds) => {
		reportDoc.sourcesAllowed = newSourceTypedIds;
		reportDocService.update(reportDocId, reportDoc);
		rerender();
	};
	const setQuestionFilter = (newQuestionFilter) => {
		reportDoc.questionFilter = newQuestionFilter;
		reportDocService.update(reportDocId, reportDoc);
		rerender();
	};

	const fullQuestionList = null; // TODO for numbers that work across rounds

	const [format, _setFormat] = useState();
	const setFormat = (newFormat) => {
		_setFormat(newFormat);
		reportDoc.title = toTitleCase(newFormat);
	};
	if (step === "FORMAT") {
		nextDisabled = !format;
	} else if (step === "GENERATING") {
		nextDisabled = reportDoc.state === LifecycleState.PROCESSING;
	}

	const [additionalInstruction, setAdditionalInstruction] = useState();
	if (isLoading) {
		return (
			<div>
				<LocalSpinner />
			</div>
		);
	}

	if (step === "EDIT_REPORT") {
		return (
			<WideMarginsWrapper sidebarWidth={0} bg={colors.esg_gray[100]}>
				<ReportDocEditorPanel reportDocId={reportDoc.id} />
			</WideMarginsWrapper>
		);
	}

	return (
		<IssbContainer>
			{step === "GENERATING" && <GeneratingPanel reportDoc={reportDoc} setStepIndex={setStepIndex} />}
			{step !== "GENERATING" && (
				<WizardStepWrapper
					title={false}
					nextText={step === "FORMAT" ? "Generate" : "Next"}
					previousText="Previous"
					gotoNext={gotoNext}
					gotoPrevious={() => setStepIndex(stepIndex - 1)}
					stepIndex={stepIndex}
					steps={STEPS}
					nextDisabled={nextDisabled}
				>
					{/* TODO: local tweat, to remove back */}
					{step === "SELECT_SOURCES" && (
						<ReportDocSourcesPanel
							setNextDisabled={setNextDisabled}
							sourceTypedIds={sourceTypedIds}
							setSourceTypedIds={setSourceTypedIds}
						/>
					)}
					{step === "SELECT_QUESTIONS" && (
						<>
							<PageHeader
								superTitle="Report Generator"
								title="Select Disclosure Questions"
								subTitle="Select the disclosures (questions) from the selected rounds that the report can draw on."
							/>
							<Box mt={2}></Box>
							<AIReviewQuestionsPanel
								helpText=""
								questionIds={questionIds}
								setQuestionIds={setQuestionIds}
								fullQuestionList={fullQuestionList}
								questionFilter={reportDoc?.questionFilter}
								hideNumbers={true}
								setQuestionFilter={setQuestionFilter}
							/>
						</>
					)}
					{step === "FORMAT" && (
						<FormatPanel
							format={format}
							setFormat={setFormat}
							additionalInstruction={additionalInstruction}
							setAdditionalInstruction={setAdditionalInstruction}
							reportDoc={reportDoc}
						/>
					)}
				</WizardStepWrapper>
			)}
		</IssbContainer>
	);
}

const BLANK_REPORT_FORMAT = "Blank report";
const FORMATS = [
	"Board summary sustainability performance",
	"Sustainability section for annual report",
	"Shareholders report",
	//   "TCFD report from template",
	"TCFD report",
	BLANK_REPORT_FORMAT
];

const canonicalize = (formatOption) => {
	if (!formatOption) return "";
	return formatOption.toLowerCase().replace(/\s+/g, " ").trim();
};

const TEMPLATE4FORMAT = {};
TEMPLATE4FORMAT[canonicalize("TCFD report")] = TCFD_REPORT_TEMPLATE;
/**
 * HACK seed e.g. "TCFD Report" with a template for that doc
 * @param {*} formatOption
 */
const getTemplate = (formatOption) => {
	// TODO: get the template for this format
	const template = TEMPLATE4FORMAT[canonicalize(formatOption)];
	return template;
};

function FormatPanel({ format, setFormat, additionalInstruction, setAdditionalInstruction, reportDoc }) {
	// Set the format - and also set a skeleton for the report content
	const setFormatAndTemplate = (formatOption) => {
		setFormat(formatOption);
		// Do we have a template for this format?
		const template = getTemplate(formatOption);
		const DATA_AND_TIME = "Write draft content from the available data.\nTake time, and make as close to a complete draft as possible. Partial answers are acceptable when data is not available.";
		let newAdditionalInstruction = "Create a draft document using common headings for a " + formatOption + ".\n" + DATA_AND_TIME;
		if (template) {
			let headings = template.match(/<h[1-6]>(.*?)<\/h[1-6]>/g);
			if (headings) {
				let htext = ""
				headings.forEach(h => {
					let hn = parseInt(h[2]) - 1;
					let indent = "   ".repeat(hn);
					let hLabel = h.substring(4, h.length - 5);
					// no tags
					hLabel = hLabel.replace(/<[^>]*>/g, "");
					// HACK convert (hack: some) html entities to plain text
					hLabel = hLabel.replace(/&amp;/g, "&");
					hLabel = hLabel.replace(/&\w+;/g, "");
					htext += indent + "- " + hLabel + "\n";
				});
				newAdditionalInstruction = "Create a draft document for a " + formatOption + ". Write draft content from the available data for:\n" + htext + "\n\n" + DATA_AND_TIME;
			}
		}
		if (formatOption === BLANK_REPORT_FORMAT) {
			newAdditionalInstruction = "Create a blank draft document.";
		}
		setAdditionalInstruction(newAdditionalInstruction);
		// reportDoc.content = template; // replaced with additionalInstruction
		// let the AI know for when it generates the report
		updateAIContext({ canvasText: template });
	};

	return (
		<>
			<PageHeader
				icon={<Sparkle weight="fill" size={32} color={colors.navIconLime} />}
				superTitle="Content Generator"
				title="Format"
			// subTitle="Describe the type of report you want to generate. Include information about stakeholders, structure, and content."
			/>
			<H3>What type of content are you working on?</H3>
			<Box display="flex" flexWrap="wrap" gap={2}>
				{FORMATS.map((formatOption) => (
					<Button
						key={formatOption}
						variant={format === formatOption ? "primary" : "secondary"}
						display={"inline-block"}
						size="sm"
						onClick={() => setFormatAndTemplate(formatOption)}
					>
						{formatOption}
					</Button>
				))}
			</Box>
			<PropInput item={reportDoc} prop="title" label={<H3>Title</H3>} />
			{additionalInstruction && <><H3 mt={4}>Instructions for the AI</H3>
				<Textarea
					value={additionalInstruction}
					onChange={(e) => setAdditionalInstruction(e.target.value)}
					placeholder="Add guidance notes for the first AI draft."
					rows={Math.max(4, Math.min(additionalInstruction.split("\n").length, 20))}
				/></>}
		</>
	);
}

function GeneratingPanel({ reportDoc, setStepIndex }) {
	// For routing purpose
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const toast = useToast();
	const { confirm } = useConfirmation();

	const isError = reportDoc.state === LifecycleState.ERROR;

	// For deleting the reportDoc object as a whole (It does not stop the backend process tho)
	const doDeleteDuringGeneratingStep = async () => {
		const confirmed = await confirm({
			title: "Delete Report " + reportDoc.title || "",
			message: "Are you sure you want to delete this report?",
			confirmText: "Delete",
			cancelText: "Cancel",
		});
		if (!confirmed) return;
		reportDocService.delete(reportDoc.id).then(() => {
			showSuccessToast(toast, "Report doc deleted", "report-doc-deleted");
			queryClient.invalidateQueries({ queryKey: ["reportDoc"] });
			navigate(routes.issbReportDoc);
		});
	};

	return (
		<>
			<PageHeader
				icon={<Sparkle weight="fill" size={32} color={colors.navIconLime} />}
				superTitle="Report Generator"
				title="Generating"
				subTitle={isError ? "There was an error" : "Stand by..."}
			/>
			<Box
				sx={{
					height: "auto",
					width: "auto",
					padding: 5,
					backgroundColor: `${colors.lightLime}`,
					borderRadius: "10px",
					border: `solid 1px ${colors.lime}`,
					// padding: 5,
					display: "flex",
					justifyContent: "flex-start",
					alignItems: "center",
					gap: 10,
				}}
			>
				{/*<Box // off for now as the proceed-to-edit-when-its-ready behaviour is currently unreliable
		// TODO fix the update bug
          height={"70px"} // HACK : used measurement strictly according to design
          width={"50px"} // HACK : used measurement strictly according to design
        >
          <LocalSpinner />
        </Box>*/}

				<Box
					sx={{
						height: "100%",
						width: "100%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "flex-start",
						gap: 5,
					}}
				>
					{!isError && <Text color={colors.lime} fontSize={"medium"} fontWeight={"bold"}>
						We're busy making your report. It will be available for editing in a few minutes.
					</Text>}
					{isError && <Text color={colors.esg_red} fontSize={"medium"} fontWeight={"bold"}>
						{"Sorry: There was an error generating the report."}
						{reportDoc.content && "Useful content is available in the unready draft report."}
					</Text>}

					<Box display={"flex"} width={"100%"} gap={3}>
						<Button
							sx={{
								backgroundColor: `${colors.lime}`,
								color: `${colors.pureBlack}`,
							}}
							onClick={() => {
								navigate(routes.issbReportDoc);
							}}
						>
							Ok, I'm done here
						</Button>
						{/* TODO a cancel button */}
						<Button
							onClick={() => {
								if (reportDoc) {
									doDeleteDuringGeneratingStep();
								}
							}}
						>
							Delete
						</Button>
						<DevOnly>
							<Button
								onClick={() => {
									setStepIndex(4);
								}}
							>
								View Unready Report
							</Button>
						</DevOnly>
					</Box>
				</Box>
			</Box>
		</>
	);
}
